import React, { ReactElement, ReactNode } from 'react'
import { Typography } from 'antd'
import { PiCalendarBlank, PiCheck, PiGridFour, PiMapPin, PiUploadSimple } from 'react-icons/pi'
import { OnboardingStep } from './types'
import IntlMessages from '../../util/IntlMessages'

const { Title, Text } = Typography

const STEPS: { key: OnboardingStep; labelId: string; icon: ReactElement }[] = [
  { key: 'locations', labelId: 'onboarding.setup.steps.locations', icon: <PiMapPin size={16} /> },
  { key: 'leaveTypes', labelId: 'onboarding.setup.steps.leaveTypes', icon: <PiCalendarBlank size={16} /> },
  { key: 'departments', labelId: 'onboarding.setup.steps.departments', icon: <PiGridFour size={16} /> },
  { key: 'import', labelId: 'onboarding.setup.steps.import', icon: <PiUploadSimple size={16} /> },
]

const STEP_INDEX: Record<OnboardingStep, number> = {
  locations: 0,
  leaveTypes: 1,
  departments: 2,
  import: 3,
}

interface OnboardingLayoutProps {
  currentStep: OnboardingStep
  companyName: string
  firstName: string
  savedToDB: boolean
  onStepClick: (step: OnboardingStep) => void
  children: ReactNode
}

const OnboardingLayout = ({ currentStep, companyName, firstName, savedToDB, onStepClick, children }: OnboardingLayoutProps): ReactElement => {
  const currentIndex = STEP_INDEX[currentStep]

  return (
    <div className="vt-onboarding-wizard">
      <div className="vt-onboarding-inner">
        <div className="vt-onboarding-steps">
          {STEPS.map((step, index) => {
            const isCompleted = index < currentIndex
            const isActive = index === currentIndex
            const isClickable = isCompleted || savedToDB
            const statusClass = isCompleted ? 'vt-step--completed' : isActive ? 'vt-step--active' : 'vt-step--pending'

            return (
              <React.Fragment key={step.key}>
                <div
                  className={`vt-step-item ${statusClass}${isClickable ? ' vt-step-item--clickable' : ''}`}
                  onClick={isClickable ? () => onStepClick(step.key) : undefined}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onKeyDown={
                    isClickable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') onStepClick(step.key)
                        }
                      : undefined
                  }
                >
                  <div className="vt-step-icon">{isCompleted ? <PiCheck size={16} /> : step.icon}</div>
                  <span className="vt-step-label">
                    <IntlMessages id={step.labelId} />
                  </span>
                </div>
                {index < STEPS.length - 1 && <div className={`vt-step-connector${isCompleted ? ' vt-step-connector--done' : ''}`} />}
              </React.Fragment>
            )
          })}
        </div>

        <Title level={2} className="vt-onboarding-title">
          <IntlMessages id="onboarding.setup.layout.title" values={{ companyName }} />
        </Title>
        <Text className="vt-onboarding-subtitle">
          <IntlMessages id="onboarding.setup.layout.subtitle" values={{ firstName }} />
        </Text>

        <div className="vt-onboarding-content">{children}</div>
      </div>
    </div>
  )
}

export default OnboardingLayout
